import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Trans, useLingui } from "@lingui/react/macro";
import { api, ApiError, configCompanies, type ConfigCompany } from "../api.js";
import { useToken } from "../auth.js";

// Draft-config form. Only non-secret fields — passwords are entered later on the
// SealSecrets screen against a specific machine's public key. This deliberately
// keeps password fields off this component so a browser autocomplete can't sneak
// one in and get sent to the cloud in the clear.
//
// A customer may run several Sage databases, so this edits a LIST of companies. The
// form is prefilled from the customer's newest config version and every submit writes
// a complete new version — configs are immutable snapshots, not patches.

interface CompanyDraft {
  id: string;
  label: string;
  sqlServer: string;
  database: string;
  dbUser: string;
  dbEncrypt: boolean;
  dbTrustServerCertificate: boolean;
  dbMaxRows: number;
  webApiUrl: string;
  username: string;
  tenant: string;
  company: string;
  apiVersion: string;
  // Soft-delete: `true` retires the company from active use — filtered out of the
  // picker for new conversations, refused at start_turn, but the row stays in the
  // payload so a locked conversation can still display its label. The
  // Désactiver / Réactiver button on each row flips this.
  disabled: boolean;
  // The id this row was prefilled with, or null for a row the operator just added.
  // Renaming a prefilled id breaks the sealed-secret carry-over (keys are `<id>.*`),
  // which is invisible until an agent fails to apply — so we warn about it inline.
  originalId: string | null;
  // false = the id follows the display name (the common case: the operator never sees
  // the field). true = the operator took it over, either by clicking "Edit" or because
  // the row was prefilled from a published config, where the id is load-bearing for the
  // sealed secrets and must never drift on its own.
  idManual: boolean;
}

// Company ids prefix the sealed-secret keys and land in `.config/config/current.json`,
// so keep them to a plain lowercase slug. Server side caps them at 64 chars.
function slugifyCompanyId(label: string): string {
  return label
    .normalize("NFD")
    .replace(/\p{Mn}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .slice(0, 64)
    .replace(/^-+|-+$/g, "");
}

// Never returns an empty string: the id is required, and a blank one would only surface
// as an opaque 400 from the cloud.
function autoCompanyId(label: string, index: number): string {
  return slugifyCompanyId(label) || (index === 0 ? "default" : `company-${index + 1}`);
}

function emptyCompany(id = ""): CompanyDraft {
  return {
    id,
    label: "",
    sqlServer: "",
    database: "",
    dbUser: "",
    dbEncrypt: true,
    dbTrustServerCertificate: false,
    dbMaxRows: 1000,
    webApiUrl: "",
    username: "",
    tenant: "",
    company: "",
    apiVersion: "1.0",
    disabled: false,
    originalId: null,
    idManual: false,
  };
}

function fromPayload(c: ConfigCompany): CompanyDraft {
  return {
    id: c.id,
    label: c.label ?? "",
    sqlServer: c.sqlServer ?? "",
    database: c.database ?? "",
    dbUser: c.dbUser ?? "",
    dbEncrypt: c.dbEncrypt ?? true,
    dbTrustServerCertificate: c.dbTrustServerCertificate ?? false,
    dbMaxRows: c.dbMaxRows ?? 1000,
    webApiUrl: c.baseUrl ?? "",
    username: c.username ?? "",
    tenant: c.tenant ?? "",
    company: c.company ?? "",
    apiVersion: c.apiVersion ?? "1.0",
    disabled: c.disabled ?? false,
    originalId: c.id,
    // A published id is load-bearing (sealed-secret keys are `<id>.*`), so it never
    // follows a display-name edit on its own — the operator has to ask for it.
    idManual: true,
  };
}

export function NewConfig() {
  const { id: customerId } = useParams<{ id: string }>();
  const token = useToken();
  const navigate = useNavigate();
  const { t } = useLingui();

  const [companies, setCompanies] = useState<CompanyDraft[]>([emptyCompany("default")]);
  const [activeCompanyId, setActiveCompanyId] = useState("default");
  const [documentStorageQuotaMiB, setDocumentStorageQuotaMiB] = useState(2048);
  const [loading, setLoading] = useState(true);
  const [prefilledFrom, setPrefilledFrom] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Prefill from the live config for this customer. Since migration 057 there is
  // exactly one row per customer — no more "latest version" walk.
  const prefill = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const { config } = await api.getConfig(token, customerId);
      const existing = configCompanies(config ?? undefined);
      if (existing.length > 0) {
        setCompanies(existing.map(fromPayload));
        setActiveCompanyId(config?.payload?.sage?.activeCompanyId ?? existing[0].id);
        setDocumentStorageQuotaMiB(
          Math.round((config?.payload?.documents?.storageQuotaBytes ?? 2 * 1024 ** 3) / 1024 ** 2),
        );
        setPrefilledFrom(config?.version ?? null);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [customerId, token]);

  useEffect(() => {
    void prefill();
  }, [prefill]);

  const patch = (index: number, changes: Partial<CompanyDraft>) => {
    const current = companies[index];
    if (!current) return;
    const next = { ...current, ...changes };
    // The id trails the display name until the operator takes it over.
    if (changes.id === undefined && changes.label !== undefined && !next.idManual) {
      next.id = autoCompanyId(next.label, index);
    }
    // Renaming a company has to drag activeCompanyId along. The first row starts life
    // as "default", so without this the very common "rename the default row, then add a
    // second company" flow submits an activeCompanyId that names nothing and the cloud
    // refuses the whole payload.
    if (next.id !== current.id) {
      setActiveCompanyId((active) => (active === current.id ? next.id : active));
    }
    setCompanies((prev) => prev.map((c, i) => (i === index ? next : c)));
  };

  // "Edit" hands the id to the operator; "Use the generated id" hands it back and
  // recomputes it from the display name on the spot.
  const setIdManual = (index: number, manual: boolean) => {
    const current = companies[index];
    if (!current) return;
    patch(
      index,
      manual
        ? { idManual: true }
        : { idManual: false, id: autoCompanyId(current.label, index) },
    );
  };

  // A customer's second database usually differs from the first by a field or two, so a
  // new row starts as a copy of the one above it. Everything that identifies the company
  // — display name, id, and the link to already-sealed secrets — is left blank instead.
  const addCompany = () =>
    setCompanies((prev) => {
      const previous = prev[prev.length - 1];
      const base = previous ? { ...previous } : emptyCompany();
      return [
        ...prev,
        {
          ...base,
          label: "",
          id: autoCompanyId("", prev.length),
          idManual: false,
          originalId: null,
        },
      ];
    });

  // Build the PUT payload from a companies list + active id. Kept here rather than
  // inlined so the "Save" submit and the one-click Deactivate/Reactivate flow send
  // the exact same shape — a divergence between the two is what would let a save
  // path drop `disabled` while the toggle path kept it, or vice versa.
  const buildPayload = (rows: CompanyDraft[], active: string) => {
    const ids = rows.map((c) => c.id.trim());
    // Same fallback the submit path uses: activeCompanyId must name one of the
    // rows or the cloud refuses the whole PUT.
    const resolvedActive = ids.includes(active) ? active : ids[0];
    return {
      documents: { storageQuotaBytes: documentStorageQuotaMiB * 1024 ** 2 },
      sage: {
        companies: rows.map((c) => ({
          id: c.id.trim(),
          label: c.label.trim() || undefined,
          sqlServer: c.sqlServer || undefined,
          database: c.database || undefined,
          dbUser: c.dbUser || undefined,
          dbEncrypt: c.dbEncrypt,
          dbTrustServerCertificate: c.dbTrustServerCertificate,
          dbMaxRows: c.dbMaxRows,
          baseUrl: c.webApiUrl || undefined,
          username: c.username || undefined,
          tenant: c.tenant || undefined,
          company: c.company || undefined,
          apiVersion: c.apiVersion,
          disabled: c.disabled || undefined,
        })),
        activeCompanyId: resolvedActive,
      },
    };
  };

  const duplicateIds = (() => {
    const seen = new Set<string>();
    const dupes = new Set<string>();
    for (const c of companies) {
      const id = c.id.trim();
      if (id.length === 0) continue;
      if (seen.has(id)) dupes.add(id);
      seen.add(id);
    }
    return dupes;
  })();

  // Toggle a company's disabled flag in the local form state. The commit is
  // driven by the Save button below, same as any other edit — and Save already
  // pushes the new payload to every machine over the agent-stream WS
  // (broadcastConfigToAllMachines in admin-config.ts), so no reseal is needed to
  // propagate a deactivate/reactivate.
  //
  // Bump the default off a row we're about to disable: the cloud refuses an
  // active id that points at a disabled row, and the operator shouldn't have to
  // fix that by hand.
  const toggleDisabled = (index: number) => {
    const current = companies[index];
    if (!current) return;
    const nextDisabled = !current.disabled;
    const nextCompanies = companies.map((c, i) =>
      i === index ? { ...c, disabled: nextDisabled } : c,
    );
    setCompanies(nextCompanies);
    if (nextDisabled && current.id === activeCompanyId) {
      const fallback = nextCompanies.find((c) => !c.disabled);
      if (fallback) setActiveCompanyId(fallback.id);
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!customerId) return;
    if (duplicateIds.size > 0) {
      // Also refused server-side. Company ids prefix the sealed-secret keys, so two
      // companies sharing one would read each other's credentials.
      setError(t`Two companies share the same id: ${[...duplicateIds].join(", ")}`);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = buildPayload(companies, activeCompanyId);
      const { config } = await api.putConfig(token, customerId, payload);
      navigate(`/customers/${customerId}`, {
        state: { justCreated: config.version },
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!customerId) return null;
  if (loading) return <p className="muted"><Trans>Loading…</Trans></p>;

  return (
    <div className="detail">
      <header className="page-header">
        <div>
          <h1><Trans>Remote config</Trans></h1>
          <p className="muted">
            <Link to={`/customers/${customerId}`}><Trans>← Back to customer</Trans></Link>
          </p>
        </div>
      </header>

      {prefilledFrom !== null ? (
        <p className="muted small">
          <Trans>
            Editing the live config (revision {prefilledFrom}). Saving fans the change
            out to every connected machine of this customer; sealed passwords stay put.
          </Trans>
        </p>
      ) : (
        <p className="muted small">
          <Trans>
            No config yet for this customer. Saving creates the first revision and
            pushes it out.
          </Trans>
        </p>
      )}

      <form onSubmit={submit} className="config-form">
        <fieldset>
          <legend><Trans>Document storage</Trans></legend>
          <label>
            <Trans>Local storage quota (MiB)</Trans>
            <input
              type="number"
              min={1}
              max={1048576}
              value={documentStorageQuotaMiB}
              onChange={(e) =>
                setDocumentStorageQuotaMiB(Math.max(1, Number(e.target.value) || 1))
              }
            />
          </label>
          <p className="muted small">
            <Trans>
              Applies to conversation source files and generated documents on each customer machine.
            </Trans>
          </p>
        </fieldset>
        {companies.map((c, index) => (
          <fieldset key={index} data-disabled={c.disabled ? "true" : undefined}>
            <legend>
              <Trans>Sage 300 company {index + 1}</Trans>
              {c.label.trim() ? ` — ${c.label.trim()}` : ""}
              {c.disabled && (
                <>
                  {" — "}
                  <span className="badge warn"><Trans>Disabled</Trans></span>
                </>
              )}
            </legend>

            <label>
              <Trans>Display name</Trans>
              <input
                value={c.label}
                onChange={(e) => patch(index, { label: e.target.value })}
                placeholder={t`Accounting — Montreal`}
              />
            </label>
            <p className="muted small">
              <Trans>Shown to the user in the app when they pick a company.</Trans>
            </p>

            {c.idManual ? (
              <>
                <label>
                  <Trans>Company id (local slug)</Trans>
                  <input
                    required
                    value={c.id}
                    onChange={(e) => patch(index, { id: e.target.value })}
                  />
                </label>
                <p className="muted small computed-id">
                  <button
                    type="button"
                    className="link-btn"
                    onClick={() => setIdManual(index, false)}
                  >
                    <Trans>Use the id generated from the display name</Trans>
                  </button>
                </p>
              </>
            ) : (
              <p className="muted small computed-id">
                <Trans>Company id (local slug):</Trans> <code>{c.id}</code>{" "}
                <button
                  type="button"
                  className="link-btn"
                  onClick={() => setIdManual(index, true)}
                >
                  <Trans>Edit</Trans>
                </button>
              </p>
            )}
            {duplicateIds.has(c.id.trim()) && (
              <p className="banner err">
                <Trans>
                  This id is used by another company. Ids prefix the sealed secret keys,
                  so two companies cannot share one.
                </Trans>
              </p>
            )}
            {c.originalId !== null && c.originalId !== c.id.trim() && (
              <p className="banner err">
                <Trans>
                  Renaming "{c.originalId}" will orphan its sealed passwords — every
                  machine will have to be re-sealed for this company.
                </Trans>
              </p>
            )}

            <label className="inline">
              <input
                type="radio"
                name="activeCompany"
                checked={activeCompanyId === c.id}
                onChange={() => setActiveCompanyId(c.id)}
                // A disabled row cannot be the default — new conversations
                // would land on an unusable company.
                disabled={c.disabled}
              />{" "}
              <Trans>Default company for this customer</Trans>
            </label>

            <h4><Trans>SQL Server (read-only)</Trans></h4>
            <label>
              <Trans>Server</Trans>
              <input
                value={c.sqlServer}
                onChange={(e) => patch(index, { sqlServer: e.target.value })}
              />
            </label>
            <label>
              <Trans>Database</Trans>
              <input
                value={c.database}
                onChange={(e) => patch(index, { database: e.target.value })}
              />
            </label>
            <label>
              <Trans>DB user</Trans>
              <input
                value={c.dbUser}
                onChange={(e) => patch(index, { dbUser: e.target.value })}
              />
            </label>
            <label>
              <Trans>Max rows per query</Trans>
              <input
                type="number"
                min={1}
                max={100000}
                value={c.dbMaxRows}
                onChange={(e) =>
                  patch(index, { dbMaxRows: Math.max(1, Number(e.target.value) || 1000) })
                }
              />
            </label>
            <label className="inline">
              <input
                type="checkbox"
                checked={c.dbEncrypt}
                onChange={(e) => patch(index, { dbEncrypt: e.target.checked })}
              />{" "}
              <Trans>Encrypt TLS (recommended — keep on)</Trans>
            </label>
            <label className="inline">
              <input
                type="checkbox"
                checked={c.dbTrustServerCertificate}
                onChange={(e) =>
                  patch(index, { dbTrustServerCertificate: e.target.checked })
                }
              />{" "}
              <Trans>
                Trust server certificate (required on stock Sage 300 installs with a
                self-signed SQL Server cert)
              </Trans>
            </label>

            <h4><Trans>Sage Web API</Trans></h4>
            <label>
              <Trans>Base URL</Trans>
              <input
                value={c.webApiUrl}
                onChange={(e) => patch(index, { webApiUrl: e.target.value })}
                placeholder="http://accpac/Sage300WebApi"
              />
            </label>
            <label>
              <Trans>Username</Trans>
              <input
                value={c.username}
                onChange={(e) => patch(index, { username: e.target.value })}
              />
            </label>
            <label>
              <Trans>Tenant</Trans>
              <input
                value={c.tenant}
                onChange={(e) => patch(index, { tenant: e.target.value })}
              />
            </label>
            <label>
              <Trans>Company (Sage code)</Trans>
              <input
                value={c.company}
                onChange={(e) => patch(index, { company: e.target.value })}
              />
            </label>
            <label>
              <Trans>API version</Trans>
              <input
                value={c.apiVersion}
                onChange={(e) => patch(index, { apiVersion: e.target.value })}
              />
            </label>

            <div className="actions">
              {c.originalId === null ? (
                // A row the operator just added and never saved: safe to drop
                // locally without a round-trip. "Deactivate" wouldn't fit here —
                // there's nothing on the server to deactivate.
                <button
                  type="button"
                  className="ghost"
                  onClick={() =>
                    setCompanies((prev) => prev.filter((_, i) => i !== index))
                  }
                >
                  <Trans>Discard this new company</Trans>
                </button>
              ) : c.disabled ? (
                <button
                  type="button"
                  className="ghost"
                  onClick={() => toggleDisabled(index)}
                >
                  <Trans>Reactivate this company</Trans>
                </button>
              ) : (
                <button
                  type="button"
                  className="ghost"
                  onClick={() => toggleDisabled(index)}
                  title={t`Conversations that use this company become read-only. The row stays in the config so it can be reactivated later; sealed passwords are kept.`}
                >
                  <Trans>Deactivate this company</Trans>
                </button>
              )}
            </div>
          </fieldset>
        ))}

        <div className="actions">
          <button type="button" className="ghost" onClick={addCompany}>
            <Trans>Add a company</Trans>
          </button>
        </div>

        <p className="muted small">
          <Trans>
            Passwords are not set here. Once this config is saved, use "Seal secrets"
            on each machine to choose which companies that machine may query and send
            their credentials encrypted.
          </Trans>
        </p>
        <p className="muted small">
          <Trans>
            Features and write mode are set on the licence, not on the remote config.
            Edit the licence from the customer page to change what the MCP is allowed
            to touch on this customer's machines.
          </Trans>
        </p>

        {error && <div className="banner err">{error}</div>}

        <div className="actions">
          <Link to={`/customers/${customerId}`}>
            <button type="button" className="ghost">
              <Trans>Cancel</Trans>
            </button>
          </Link>
          <button type="submit" disabled={submitting || duplicateIds.size > 0}>
            {submitting ? <Trans>Saving…</Trans> : <Trans>Save</Trans>}
          </button>
        </div>
      </form>
    </div>
  );
}
